import { useEffect, useRef, useState } from "react";
import css from "./SpecVersionPicker.module.css";

/**
 * Пикер спецверсии (A46-коррекция: нативный select ОС снят) — по образцу
 * родни модалки SourcePicker/MkPicker: кнопка-триггер со значением +
 * `.cyber-pop` разворачивающийся список. «Без версии» — первый пункт
 * (сбрасывает выбор, как «Без МК» в MkPicker), текущая версия подсвечена.
 *
 * Триггер получает класс поля от родителя (`className` → css.input формы),
 * чтобы выглядеть как остальные поля ряда.
 */
export interface SpecVersionPickerProps {
  /** Текущий special_version id ("" — без версии). */
  value: string;
  /** Все спецверсии сети (id + title) — не зависят от локации. */
  options: { id: string; title: string }[];
  onPick: (id: string) => void;
  /** Класс поля-триггера от родителя (шелл .input формы). */
  className?: string;
}

export function SpecVersionPicker({ value, options, onPick, className = "" }: SpecVersionPickerProps) {
  const rootRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);

  /* Outside-click + Esc close — как у родни (SourcePicker/MkPicker). */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  const current = options.find((o) => o.id === value);
  /* «Без версии» первым пунктом, дальше — все спецверсии. */
  const rows = [{ id: "", title: "Без версии" }, ...options];

  return (
    <div ref={rootRef} className={css.wrap}>
      <button
        type="button"
        className={`${className} ${css.trigger} ${current ? "" : css.triggerEmpty}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Спецверсия"
      >
        {current ? current.title : "— выбери спецверсию —"}
      </button>
      {open ? (
        <div className={css.pop} role="dialog" aria-label="Выбор спецверсии">
          <div className={css.list}>
            {rows.map((opt) => {
              const selected = opt.id === value;
              return (
                <button
                  key={opt.id || "_none_"}
                  type="button"
                  className={`${css.opt} ${selected ? css.selected : ""} ${opt.id ? "" : css.clearOpt}`}
                  onClick={() => {
                    onPick(opt.id);
                    setOpen(false);
                  }}
                >
                  <span className={css.chev} aria-hidden="true">›</span>
                  <span className={css.optName}>{opt.title}</span>
                </button>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
